import { useCallback } from "react";

import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectActiveClassSessionId, selectCurrentSectionId } from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { selectScreensharingClassUserId } from "store/selectors/virtual-classes";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { dismissPausedPresentation, resumePresentation, usePresentation } from "utils/presentation";
import { useNavigate } from "utils/routing";

export const useResumePresentation = () => {
  const navigate = useNavigate();
  const { canAuthorSection } = useCapabilities();
  const classSessionId = useAppSelector(selectActiveClassSessionId);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const { isPresentationPaused } = usePresentation();

  const showResumePresentationBanner = Boolean(
    canAuthorSection && !isInBreakout && isPresentationPaused && !screensharingClassUserId,
  );

  const onResumePresentation = useCallback(async () => {
    if (!classSessionId || !sectionId) return;

    try {
      await resumePresentation(classSessionId, sectionId, navigate);
    } catch (error) {
      toastLocalizedOperationCatcher("present_page_failure")(error);
    }
  }, [classSessionId, sectionId, navigate]);

  const onDismissPausedPresentation = useCallback(async () => {
    if (!classSessionId) return;

    try {
      await dismissPausedPresentation(classSessionId);
    } catch (error) {
      toastLocalizedOperationCatcher("stop_presenting_failure")(error);
    }
  }, [classSessionId]);

  return { showResumePresentationBanner, onResumePresentation, onDismissPausedPresentation };
};
